// components/WorkloadBalanceGrid.js
import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Chip,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
  Avatar
} from '@material-ui/core';
import {
  MoreVert,
  TrendingUp,
  Schedule,
  Warning,
  CheckCircle,
  Group
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  gridContainer: {
    flex: 1,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '10px',
    }
  },
  gridTable: {
    minWidth: 600,
    borderCollapse: 'collapse',
    '& th': {
      padding: theme.spacing(1),
      textAlign: 'center',
      backgroundColor: theme.palette.grey[100],
      borderBottom: `2px solid ${theme.palette.divider}`,
      fontWeight: 'bold',
      position: 'sticky',
      top: 0,
      zIndex: 1
    },
    '& td': {
      padding: theme.spacing(1),
      textAlign: 'center',
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderRight: 'none'
      }
    }
  },
  crewCell: {
    textAlign: 'left !important',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    position: 'sticky',
    left: 0,
    backgroundColor: theme.palette.background.paper,
    borderRight: `2px solid ${theme.palette.divider}`,
    minWidth: 150
  },
  dayCell: {
    cursor: 'pointer',
    position: 'relative',
    minWidth: 80,
    height: 60,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  hoursChip: {
    fontWeight: 'bold',
    minWidth: 60
  },
  utilizationBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: theme.palette.grey[300]
  },
  summaryRow: {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 'bold',
    '& td': {
      borderTop: `2px solid ${theme.palette.divider}`
    }
  },
  legendSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  }
}));

const WorkloadBalanceGrid = ({ crewMetrics, selectedDate, onDayClick, onCrewClick }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const currentDay = moment(selectedDate).format('dddd');

  // Get hours color based on workload
  const getHoursColor = (hours) => {
    if (hours === 0) return '#e0e0e0'; // No work
    if (hours <= 6) return '#4caf50'; // Light day
    if (hours <= 8) return '#2196f3'; // Normal day
    if (hours <= 10) return '#ff9800'; // Heavy day
    return '#f44336'; // Overtime
  };

  // Get chip color variant
  const getChipColor = (hours) => {
    if (hours === 0) return 'default';
    if (hours <= 8) return 'primary';
    if (hours <= 10) return 'secondary';
    return 'error';
  };

  // Calculate daily totals
  const dailyTotals = {};
  const dailyCounts = {};
  days.forEach(day => {
    dailyTotals[day] = 0;
    dailyCounts[day] = 0;
  });

  // Filter and sort crews
  const activeCrews = Object.values(crewMetrics)
    .filter(crew => crew.workload.weeklyHours > 0)
    .sort((a, b) => b.workload.weeklyHours - a.workload.weeklyHours);

  // Calculate totals
  activeCrews.forEach(crew => {
    days.forEach(day => {
      const hours = crew.workload.dailyHours[day] || 0;
      if (hours > 0) {
        dailyTotals[day] += hours;
        dailyCounts[day]++;
      }
    });
  });

  const handleMenuOpen = (event, crew) => {
    setAnchorEl(event.currentTarget);
    setSelectedCrew(crew);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCrew(null);
  };

  const handleDayClick = (crew, day) => {
    if (onDayClick) {
      onDayClick(crew, day);
    }
  };

  return (
    <Paper className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6">
          Workload Balance Grid
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Week of {moment(selectedDate).startOf('week').format('MMM D')}
        </Typography>
      </div>

      {/* Grid Table */}
      <div className={classes.gridContainer}>
        <table className={classes.gridTable}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Crew</th>
              {days.map(day => (
                <th 
                  key={day}
                  style={{
                    backgroundColor: day === currentDay ? '#e3f2fd' : undefined,
                    fontWeight: day === currentDay ? 'bold' : 'normal'
                  }}
                >
                  {day.slice(0, 3)}
                </th>
              ))}
              <th>Total</th>
              <th>Avg/Day</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {activeCrews.map(crew => {
              const avgDaily = crew.workload.dailyAverage;
              const isOverworked = crew.workload.overtime > 4;
              const isUnderutilized = crew.workload.utilization < 60;

              return (
                <tr key={crew.crewId}>
                  <td className={classes.crewCell}>
                    <Avatar 
                      style={{ 
                        backgroundColor: crew.crewColor,
                        width: 28,
                        height: 28,
                        fontSize: '0.75rem'
                      }}
                    >
                      {crew.crewName.charAt(0)}
                    </Avatar>
                    <Typography variant="body2">
                      {crew.crewName}
                    </Typography>
                  </td>
                  
                  {days.map(day => {
                    const hours = crew.workload.dailyHours[day] || 0;
                    const utilization = hours > 0 ? Math.min((hours / 8) * 100, 100) : 0;
                    
                    return (
                      <td 
                        key={day}
                        className={classes.dayCell}
                        onClick={() => handleDayClick(crew, day)}
                        style={{
                          backgroundColor: day === currentDay ? '#f5f5f5' : 'transparent'
                        }}
                      >
                        {hours > 0 ? (
                          <Tooltip title={`${hours.toFixed(1)} hours (${Math.round(utilization)}% of 8h target)`}>
                            <Chip
                              label={hours.toFixed(1)}
                              size="small"
                              color={getChipColor(hours)}
                              className={classes.hoursChip}
                              style={{
                                backgroundColor: getHoursColor(hours),
                                color: hours > 8 ? 'white' : undefined
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            -
                          </Typography>
                        )}
                        <div 
                          className={classes.utilizationBar}
                          style={{
                            background: `linear-gradient(to right, ${getHoursColor(hours)} ${utilization}%, #e0e0e0 ${utilization}%)`
                          }}
                        />
                      </td>
                    );
                  })}
                  
                  <td>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {crew.workload.weeklyHours.toFixed(1)}h
                    </Typography>
                  </td>
                  
                  <td>
                    <Typography variant="body2">
                      {avgDaily.toFixed(1)}h
                    </Typography>
                  </td>
                  
                  <td>
                    {isOverworked ? (
                      <Tooltip title={`${crew.workload.overtime.toFixed(1)}h overtime`}>
                        <Chip
                          icon={<Warning />}
                          label="Overworked"
                          size="small"
                          color="error"
                        />
                      </Tooltip>
                    ) : isUnderutilized ? (
                      <Tooltip title={`${crew.workload.utilization}% utilized`}>
                        <Chip
                          icon={<TrendingUp />}
                          label="Available"
                          size="small"
                          color="primary"
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        icon={<CheckCircle />}
                        label="Balanced"
                        size="small"
                        color="default"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
            
            {/* Summary Row */}
            <tr className={classes.summaryRow}>
              <td style={{ textAlign: 'left', fontWeight: 'bold' }}>
                Daily Totals
              </td>
              {days.map(day => (
                <td key={`total-${day}`}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {dailyTotals[day].toFixed(0)}h
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ({dailyCounts[day]} crews)
                  </Typography>
                </td>
              ))}
              <td colSpan={3}>
                <Typography variant="body2" color="textSecondary">
                  {Object.values(dailyTotals).reduce((a, b) => a + b, 0).toFixed(0)}h total
                </Typography>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <Box className={classes.legendSection}>
        <div className={classes.legendItem}>
          <Box width={20} height={20} bgcolor="#4caf50" borderRadius={1} />
          <Typography variant="caption">Light (≤6h)</Typography>
        </div>
        <div className={classes.legendItem}>
          <Box width={20} height={20} bgcolor="#2196f3" borderRadius={1} />
          <Typography variant="caption">Normal (6-8h)</Typography>
        </div>
        <div className={classes.legendItem}>
          <Box width={20} height={20} bgcolor="#ff9800" borderRadius={1} />
          <Typography variant="caption">Heavy (8-10h)</Typography>
        </div>
        <div className={classes.legendItem}>
          <Box width={20} height={20} bgcolor="#f44336" borderRadius={1} />
          <Typography variant="caption">Overtime (>10h)</Typography>
        </div>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>View Crew Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Reassign Jobs</MenuItem>
        <MenuItem onClick={handleMenuClose}>Export Schedule</MenuItem>
      </Menu>
    </Paper>
  );
};

export default WorkloadBalanceGrid;